'use client';

import {
  Button,
  Divider,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import type { Role, Viewer } from '@repo/api-gw-sdk';
import { redirect } from 'next/navigation';
import { useEffect, useState, type PropsWithChildren } from 'react';
import { useCookie } from 'react-use';

import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { Link } from '@/components/shared/link';
import { rolesComparer } from '@/configs/roles';
import {
  getGlobalManagementAccessCookieExpiration,
  globalManagementAccessCookieName,
} from '@/contexts/environment';
import { useEnvironment } from '@/contexts/useEnvironment';
import { useRoles } from '@/contexts/useRoles';
import { useUser } from '@/contexts/useUser';

const GlobalManagementAccess = () => {
  const { rolesMap, isGlobalRole } = useRoles();
  const { user, changeRole } = useUser();
  const [, updateCookie] = useCookie(globalManagementAccessCookieName);
  const [selectedRoleId, setSelectedRoleId] = useState<string>();
  const [isAccessing, setAccessing] = useState(false);
  const { isDemo, isDev } = useEnvironment();

  const availableAccountRoles = Object.keys(user?.roleToProjectsMappings || {})
    .reduce<Role[]>((agg, roleId) => {
      const role = rolesMap[roleId];
      if (isGlobalRole(role)) {
        agg.push(role);
      }
      return agg;
    }, [])
    .sort(rolesComparer);

  if (!availableAccountRoles.length) {
    redirect('/forbidden');
  }

  const handleAccess = async () => {
    setAccessing(true);
    // Set cookie for 2 hours with same role
    updateCookie(
      (isDemo || isDev ? user?.selectedRole.id : selectedRoleId) || '',
      {
        expires: getGlobalManagementAccessCookieExpiration(),
      }
    );

    if (selectedRoleId !== user?.selectedRole.id) {
      await changeRole(selectedRoleId!);
    }

    window.location.reload();
  };

  return (
    <Stack
      gap='24px'
      className='h-full w-full max-h-screen max-w-screen'
      alignItems='center'
      justifyContent='center'
    >
      <Typography variant='h1'>Global management</Typography>
      <Typography className='w-[340px] text-center' lineHeight='20px'>
        {`You are about to access your organization's global management console. Select the correct role before entering`}
      </Typography>
      <Stack direction='row' alignItems='center' gap='12px'>
        <Select
          size='medium'
          sx={{
            '& .MuiSelect-select .notranslate::after': {
              content: `"Select role"`,
              opacity: 0.42,
            },
          }}
          placeholder='Select role'
          className='w-[170px]'
          value={selectedRoleId || ''}
          onChange={(event) => setSelectedRoleId(event.target.value)}
        >
          {availableAccountRoles.map((role) => (
            <MenuItem key={role.id} value={role.id}>
              {role.name}
            </MenuItem>
          ))}
        </Select>
        <ButtonWithIcon
          text='Access'
          data-testid='access-button'
          className='h-[48px] px-[14px] py-[8px]'
          disabled={!selectedRoleId || isAccessing}
          variant='contained'
          onClick={() => void handleAccess()}
          icon='material-symbols-arrow-right-alt'
          iconPosition='right'
        />
      </Stack>
      <Divider className='w-[600px] my-[16px]' />
      <Stack direction='row' alignItems='center' gap='12px'>
        <Typography>Changed your mind?</Typography>
        <Link href='/'>
          <Button>Go Back</Button>
        </Link>
      </Stack>
    </Stack>
  );
};

export const AccountAuthentication = ({ children }: PropsWithChildren) => {
  const { user } = useUser();
  const { rolesMap, isGlobalRole } = useRoles();
  const [accessRoleId, updateCookie] = useCookie(
    globalManagementAccessCookieName
  );
  const { isDemo, isDev } = useEnvironment();

  const role = accessRoleId ? rolesMap[accessRoleId] : undefined;
  const isAuthenticated = isUserAuthenticated(
    user,
    role,
    isDemo || isDev,
    isGlobalRole(role)
  );

  useEffect(() => {
    if (isAuthenticated && accessRoleId) {
      updateCookie(accessRoleId, {
        expires: getGlobalManagementAccessCookieExpiration(),
      });
    }
  }, [accessRoleId, isAuthenticated, updateCookie]);

  if (isAuthenticated) {
    return children;
  }

  return <GlobalManagementAccess />;
};

const isUserAuthenticated = (
  user: Viewer | undefined,
  role: Role | undefined,
  isDemoOrDev: boolean,
  isGlobalRole: boolean
) => {
  if (!role || !user) {
    return false;
  }

  if (user.selectedRole.id !== role.id) {
    return false;
  }

  if (isGlobalRole) {
    return true;
  }

  return isDemoOrDev;
};
